/* eslint-disable max-lines */

import Button from "components/buttons/Button"
import ResetButton from "components/buttons/Reset"
import ShareButton from "components/buttons/ShareButton"
import Reset from "components/Reset"
import { rgba } from "polished"
import styled, { css, keyframes } from "styled-components"
import colors from "styles/colors"
import mediaQueries from "styles/mediaQueries"
import motionTokens from "styles/motionTokens"

import type {
  CarouselSharePlacement,
  CounterPlacement,
  CounterVariant
} from "./types"

export const BUTTONS_ALPHA = 0.4
export const BUTTONS_ALPHA_HOVER = 0.3
const BUTTONS_DISABLED_MULTIPLIER = 0.6
const BUTTONS_ALPHA_DISABLED = BUTTONS_ALPHA * BUTTONS_DISABLED_MULTIPLIER

const bounceNext = keyframes`
  0% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(-${motionTokens.translate.strong});
  }
  100% {
    transform: translateX(0);
  }
`

const bouncePrev = keyframes`
  0% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(${motionTokens.translate.strong});
  }
  100% {
    transform: translateX(0);
  }
`

const slideIn = keyframes`
  from {
    opacity: 0.4;
    transform: translateY(${motionTokens.translate.medium});
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const CarouselContainer = styled.section<{
  $isOverlay?: boolean
  $bounceDirection?: "next" | "prev" | null
}>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  max-width: 100%;
  overflow: hidden;
  position: relative;
  row-gap: ${({ $isOverlay }) => ($isOverlay ? "20px" : "10px")};
  width: 100%;

  ${({ $bounceDirection }) => {
    if ($bounceDirection === "next") {
      return css`
        animation: ${bounceNext} ${motionTokens.duration.medium}ms
          ${motionTokens.easing.standard};
      `
    }

    if ($bounceDirection === "prev") {
      return css`
        animation: ${bouncePrev} ${motionTokens.duration.medium}ms
          ${motionTokens.easing.standard};
      `
    }

    return undefined
  }}

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`

export const CarouselInnerUl = styled(Reset.Ul)<{
  $columnGap: number
  $slidesToShow: number
  $translateX: number
  $translateXOffset: number
}>`
  column-gap: ${({ $columnGap }) => `${$columnGap}px`};
  display: flex;
  flex-wrap: nowrap;
  transform: ${({ $translateX, $translateXOffset }) =>
    `translateX(calc(${$translateX}% - ${$translateXOffset}px))`};
  transition: transform 0.3s ease-out;
  width: calc(
    100% -
      ${({ $columnGap, $slidesToShow }) => {
        const slides = Math.max($slidesToShow, 1)
        return `${$columnGap * (slides - 1)}px`
      }}
  );

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const CarouselSlideLi = styled(Reset.Li)<{ $slidesToShow: number }>`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  position: relative;
  width: ${({ $slidesToShow }) => `calc(100% / ${Math.max($slidesToShow, 1)})`};
  opacity: 1;
  animation: ${slideIn} ${motionTokens.duration.medium}ms
    ${motionTokens.easing.standard};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`

const sharePlacementStyles = ({
  $placement
}: {
  $placement: CarouselSharePlacement
}) => {
  const isBottom = $placement.startsWith("bottom")
  const isRight = $placement.endsWith("right")

  return css`
    ${isBottom
      ? css`
          bottom: 10px;
          top: auto;
        `
      : css`
          top: 10px;
          bottom: auto;
        `}
    ${isRight
      ? css`
          right: 6px;
          left: auto;
        `
      : css`
          left: 6px;
          right: auto;
        `}

    ${mediaQueries.tablet} {
      ${isBottom
        ? css`
            bottom: 10px;
          `
        : css`
            top: 10px;
          `}
    }
  `
}

export const CarouselControls = styled.div<{
  $placement: CarouselSharePlacement
}>`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  column-gap: 6px;
  position: absolute;
  z-index: 2;
  ${sharePlacementStyles}
`

export const CarouselShareButton = styled(ShareButton).attrs({
  transparent: false
})`
  background-color: ${rgba(colors.black, BUTTONS_ALPHA)};
  border: none;
  border-radius: 0;
  color: ${({ theme }) => theme.palette.text.contrast};
  height: 32px;
  width: 32px;

  &:hover,
  &:focus-visible {
    background-color: ${rgba(colors.black, BUTTONS_ALPHA_HOVER)};
  }

  ${mediaQueries.tablet} {
    height: 36px;
    width: 36px;
  }
`

const NavButton = styled(Button)<{
  $isShowThumbnails?: boolean
}>`
  background-color: ${rgba(colors.black, BUTTONS_ALPHA)};
  border: none;
  cursor: pointer;
  padding: 8px;
  position: absolute;
  top: ${({ $isShowThumbnails }) =>
    $isShowThumbnails ? "calc((100% - 84px) / 2)" : "50%"};
  transform: translateY(-50%);

  svg {
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
    will-change: transform;
  }

  &:hover,
  &:focus-visible {
    background-color: ${rgba(colors.black, BUTTONS_ALPHA_HOVER)};
    svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:active {
    svg {
      transform: scale(${motionTokens.press.medium});
    }
  }

  &[aria-disabled="true"] {
    background-color: ${rgba(colors.black, BUTTONS_ALPHA_DISABLED)};
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: auto;

    svg {
      transform: none;
    }
  }

  ${mediaQueries.tablet} {
    top: ${({ $isShowThumbnails }) =>
      $isShowThumbnails ? "calc((100% - 92px) / 2)" : "50%"};
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`

export const NextButton = styled(NavButton)`
  right: 6px;
`

export const PrevButton = styled(NavButton)`
  left: 6px;
`

type CounterStyleProps = {
  $isShowThumbnails?: boolean
  $placement: CounterPlacement
  $variant: CounterVariant
}

const counterPlacementStyles = ({
  $isShowThumbnails,
  $placement
}: Omit<CounterStyleProps, "$variant">) => {
  const isBottom = $placement.startsWith("bottom")
  const isRight = $placement.endsWith("right")
  const bottomDesktop = $isShowThumbnails ? "84px" : "10px"
  const bottomTablet = $isShowThumbnails ? "92px" : "10px"

  return css`
    ${isBottom
      ? css`
          bottom: ${bottomDesktop};
          top: auto;
        `
      : css`
          top: 10px;
          bottom: auto;
        `}
    ${isRight
      ? css`
          right: 6px;
          left: auto;
        `
      : css`
          left: 6px;
          right: auto;
        `}

    ${mediaQueries.tablet} {
      ${isBottom
        ? css`
            bottom: ${bottomTablet};
            top: auto;
          `
        : css`
            top: 10px;
            bottom: auto;
          `}
    }
  `
}

export const Counter = styled.div.attrs<CounterStyleProps>(
  ({ $placement, $variant }) => ({
    "data-counter-placement": $placement,
    "data-counter-variant": $variant
  })
)<CounterStyleProps>`
  align-items: center;
  background-color: ${rgba(colors.black, BUTTONS_ALPHA)};
  border: none;
  color: ${colors.white};
  column-gap: 8px;
  cursor: default;
  display: grid;
  font-size: 14px;
  font-weight: 700;
  grid-template-columns: 1fr auto;
  padding: 6px 10px;
  position: absolute;
  text-align: center;
  transition: background-color 0.3s ease-out;

  &:hover {
    background-color: ${rgba(colors.black, BUTTONS_ALPHA_HOVER)};
  }

  ${({ $isShowThumbnails, $placement }) =>
    counterPlacementStyles({ $isShowThumbnails, $placement })}

  ${mediaQueries.tablet} {
    font-size: 16px;
  }
`

/**
 * Thumbnails rail
 * Horizontal overflow + snap
 */
export const ThumbnailsContainer = styled.div`
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  width: 100%;

  /* Hide scrollbar cross-browser while preserving scroll */
  /* Firefox */
  scrollbar-width: none;
  /* IE/Edge (legacy) */
  -ms-overflow-style: none;
  /* WebKit */
  &::-webkit-scrollbar {
    display: none;
    height: 0;
    width: 0;
  }
`

export const ThumbnailsUl = styled(Reset.Ul)<{ $columnGap: number }>`
  align-items: center;
  column-gap: ${({ $columnGap }) => $columnGap}px;
  display: flex;
`

export const ThumbnailsLi = styled(Reset.Li)`
  flex: 0 0 auto; /* Prevent shrinking => no squashing */
  scroll-snap-align: start;
`

export const ThumbnailButton = styled(ResetButton)`
  all: unset;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: block;
  height: 64px;
  opacity: 0.6;
  overflow: hidden;
  transform: scale(1);
  transition:
    opacity ${motionTokens.duration.short}ms ${motionTokens.easing.standard},
    transform ${motionTokens.duration.short}ms ${motionTokens.easing.standard};
  will-change: transform, opacity;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${colors.white};
    outline-offset: 2px;
    transform: scale(${motionTokens.scale.subtle});
  }

  &[aria-pressed="true"] {
    opacity: 1;
    transform: scale(${motionTokens.scale.subtle});
  }

  &:hover {
    opacity: 1;
    transform: scale(${motionTokens.scale.subtle});
  }

  &:active {
    transform: scale(0.97);
  }

  & img,
  & picture,
  & video,
  & svg {
    display: block;
    height: 100%;
    object-fit: cover;
    width: auto;
  }

  ${mediaQueries.tablet} {
    height: 72px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${motionTokens.duration.short}ms linear;

    &:hover,
    &:focus-visible,
    &:active {
      transform: none;
    }
  }
`

export const ZoomTriggerButton = styled.button`
  align-items: center;
  background: ${rgba(colors.black, BUTTONS_ALPHA)};
  border: none;
  color: ${colors.white};
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  padding: 8px 10px;
  transition: background ${motionTokens.duration.short}ms
    ${motionTokens.easing.standard};

  svg {
    transition: transform ${motionTokens.duration.short}ms
      ${motionTokens.easing.standard};
    transform-origin: center;
    will-change: transform;
  }

  &:hover,
  &:focus-visible {
    background: ${rgba(colors.black, BUTTONS_ALPHA_HOVER)};
    svg {
      transform: scale(${motionTokens.scale.medium});
    }
  }

  &:active {
    svg {
      transform: scale(${motionTokens.press.medium});
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:focus-visible,
    &:active {
      svg {
        transform: none;
      }
    }
  }
`
